/**
 * /bonus — Lyrixa Bonus, the extras bundled with eligible offers.
 *
 * Transcribed from the monolith's `view === "bonus"` block.
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, FileMusic, FileText, Gift, Layers, Music } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiErrorMessage } from '../api/errors';
import { productsApi } from '../api/productsApi';
import { Btn, LoadError, LoadSkeleton, Modal, SectionHeading } from '../components/ui';
import { useEntitlements } from '../hooks';
import { downloadTextFile } from '../lib/download';
import {
  MAX_SELECTED_VOICE_PACKS,
  useBonusPackSelectionStore,
} from '../stores/bonusPackSelectionStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import type { BonusPack, BonusSamplePrompt, BonusTextFile, BonusVoicePack } from '../types/domain';
import { paths } from './paths';

const BONUS_ICONS = {
  'lyric-templates': FileMusic,
  'voice-pack': Music,
  'sample-pack': Layers,
} as const;

const PACK_PLACEHOLDER_MESSAGE = 'Nothing for now. Packs will be distributed soon.';

type LegacyBonusPack = BonusPack & { textFile?: BonusTextFile };

function normalizedTextFiles(pack: BonusPack | null): BonusTextFile[] {
  if (!pack) return [];
  if (Array.isArray(pack.textFiles)) {
    return pack.textFiles.map((file) => ({ ...file, mimeType: 'text/plain' }));
  }
  const legacy = (pack as LegacyBonusPack).textFile;
  return legacy ? [{ ...legacy, mimeType: 'text/plain' }] : [];
}

function normalizedVoicePacks(pack: BonusPack | null): BonusVoicePack[] {
  if (!Array.isArray(pack?.voicePacks)) return [];
  return pack.voicePacks.map((voicePack) => ({
    title: typeof voicePack.title === 'string' ? voicePack.title.trim() : '',
    contents: typeof voicePack.contents === 'string' ? voicePack.contents : '',
  }));
}

function normalizedSamplePrompts(pack: BonusPack | null): BonusSamplePrompt[] {
  if (!Array.isArray(pack?.samplePrompts)) return [];
  return pack.samplePrompts.map((entry) => ({
    title: typeof entry.title === 'string' ? entry.title.trim() : '',
    prompt: typeof entry.prompt === 'string' ? entry.prompt : '',
  }));
}

export default function Bonus() {
  const navigate = useNavigate();
  const { claimedBonuses, claimBonus } = useEntitlements();
  const showToast = useUiStore((s) => s.showToast);
  const [bonuses, setBonuses] = useState<BonusPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingBonus, setViewingBonus] = useState<BonusPack | null>(null);
  const [viewingBonusId, setViewingBonusId] = useState<string | null>(null);
  const addVoicePack = useBonusPackSelectionStore((state) => state.addVoicePack);
  const selectSamplePack = useBonusPackSelectionStore((state) => state.selectSamplePack);

  /**
   * Which bonus is being claimed, so no card can be pressed twice.
   *
   * The `claimed` flag is only set after the call resolves, and `productsApi.claimBonus`
   * resolves after MOCK_LATENCY_MS — so two presses inside that window sent two claims for
   * one bonus, which on a real backend is two grants of the same content to reconcile. The
   * same guard Products.tsx and Topup.tsx carry, for the same reason.
   *
   * The id rather than a boolean, matching Products: one claim in flight disables all of
   * them, and this also answers "which one" for anything that later wants per-card wording.
   */
  const [claiming, setClaiming] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadBonuses() {
      setLoading(true);
      setError(null);
      try {
        const next = await productsApi.getBonuses();
        if (!cancelled) setBonuses(next);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, { preserveMessage: false }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadBonuses();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Claims a bonus and says so if it did not work.
   *
   * `() => void claimBonus(b.id)` discarded the rejection — `productsApi.claimBonus` can
   * throw, and the card would silently stay unclaimed with no toast and no console error,
   * which is the same dead-button defect Products.tsx documents at its own handler. The
   * flag is only flipped by the store after the call resolves, so a failure leaves the
   * card correctly unclaimed; what was missing was telling the user why.
   */
  async function handleClaim(bonusId: string): Promise<void> {
    if (claiming) return;
    setClaiming(bonusId);
    try {
      await claimBonus(bonusId);
    } catch (err) {
      showToast(apiErrorMessage(err, { preserveMessage: false }));
    } finally {
      // Released even on failure, so a claim that was refused can be tried again.
      setClaiming(null);
    }
  }

  async function handleViewPack(bonusId: string): Promise<void> {
    if (viewingBonusId) return;
    setViewingBonusId(bonusId);
    try {
      const pack = await productsApi.getBonusPack(bonusId);
      setViewingBonus(pack);
    } catch (err) {
      showToast(apiErrorMessage(err, { preserveMessage: false }));
    } finally {
      setViewingBonusId(null);
    }
  }

  function handleDownloadTextFile(file: BonusTextFile): void {
    downloadTextFile(file.fileName, file.contents, file.mimeType);
  }

  function handleDownloadAllTextFiles(files: BonusTextFile[]): void {
    files.forEach(handleDownloadTextFile);
    showToast(`Started ${files.length} download${files.length === 1 ? '' : 's'}`);
  }

  function handleUsePack(
    kind: 'voice' | 'sample',
    bonus: BonusPack,
    entryIndex: number,
    title: string
  ) {
    const selection = { kind, bonusId: bonus.id, entryIndex, title } as const;
    if (kind === 'voice') {
      const result = addVoicePack(selection);
      if (result === 'limit') {
        showToast(`You can use up to ${MAX_SELECTED_VOICE_PACKS} voice packs at once.`);
        return;
      }
      if (result === 'duplicate') showToast('That voice pack is already selected.');
    } else {
      selectSamplePack(selection);
    }
    setViewingBonus(null);
    navigate(paths.create);
  }

  const orderedBonuses = useMemo(() => {
    const unclaimed = bonuses.filter((bonus) => !claimedBonuses.includes(bonus.id));
    const claimed = bonuses.filter((bonus) => claimedBonuses.includes(bonus.id));
    return [...unclaimed, ...claimed];
  }, [bonuses, claimedBonuses]);

  const viewingTextFiles = useMemo(() => normalizedTextFiles(viewingBonus), [viewingBonus]);
  const viewingSamplePrompts = useMemo(() => normalizedSamplePrompts(viewingBonus), [viewingBonus]);
  const viewingVoicePacks = useMemo(() => normalizedVoicePacks(viewingBonus), [viewingBonus]);

  return (
    <div>
      <SectionHeading
        as="h1"
        title="Lyrixsa Bonus"
        subtitle="Claim your bundled extras and open claimed packs here."
        marginBottom={18}
      />

      {loading ? (
        <LoadSkeleton rows={3} label="Loading your bonuses…" />
      ) : error ? (
        <LoadError message={error} onRetry={() => window.location.reload()} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {orderedBonuses.map((b) => {
            const claimed = claimedBonuses.includes(b.id);
            const Icon = BONUS_ICONS[b.id as keyof typeof BONUS_ICONS] ?? Gift;
            return (
              <div
                key={b.id}
                style={{
                  // An unclaimed bonus is highlighted in accent; claiming it drops
                  // it back to the neutral surface. That is the design's way of
                  // showing what is still on the table.
                  border: claimed ? `1px solid ${c.border}` : `1px solid ${c.accentBorder}`,
                  background: claimed ? c.surface1 : c.accentSoft,
                  borderRadius: 12,
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: claimed ? c.surface3 : c.accentFill,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={17} color={claimed ? c.text2 : '#1A1300'} />
                  </div>
                  <div>
                    <p style={{ fontWeight: 500, fontSize: 13, margin: '0 0 2px' }}>{b.name}</p>
                    <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{b.desc}</p>
                  </div>
                </div>
                {claimed ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span
                      style={{
                        fontSize: 11,
                        color: c.success,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <CheckCircle2 size={13} />
                      Claimed
                    </span>
                    {b.kind !== 'credit-pack' && (
                      <Btn
                        variant="secondary"
                        onClick={() => void handleViewPack(b.id)}
                        disabled={viewingBonusId !== null}
                        style={{ flexShrink: 0 }}
                      >
                        {viewingBonusId === b.id ? 'Opening…' : 'View pack'}
                      </Btn>
                    )}
                  </div>
                ) : (
                  <Btn
                    onClick={() => void handleClaim(b.id)}
                    disabled={claiming !== null}
                    style={{ flexShrink: 0 }}
                  >
                    <Gift size={13} />
                    {claiming === b.id ? 'Claiming…' : 'Claim'}
                  </Btn>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={viewingBonus !== null}
        onClose={() => setViewingBonus(null)}
        title={viewingBonus ? `${viewingBonus.name}` : 'Bonus pack'}
        width={560}
      >
        {viewingBonus?.kind === 'lyrics-template-file' ? (
          viewingTextFiles.length > 0 ? (
            <div>
              <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 10px', lineHeight: 1.6 }}>
                Download each lyric template file or read it inline below.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  margin: '0 0 12px',
                  flexWrap: 'wrap',
                }}
              >
                <p style={{ fontSize: 11.5, color: c.text3, margin: 0 }}>
                  {viewingTextFiles.length} file{viewingTextFiles.length === 1 ? '' : 's'} in this
                  pack
                </p>
                <Btn
                  variant="secondary"
                  onClick={() => handleDownloadAllTextFiles(viewingTextFiles)}
                  style={{ flexShrink: 0 }}
                >
                  <Download size={13} />
                  Download all
                </Btn>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {viewingTextFiles.map((file, index) => (
                  <div
                    key={`${file.fileName}-${index}`}
                    style={{
                      border: `1px solid ${c.border}`,
                      borderRadius: 12,
                      background: c.surface1,
                      padding: '12px 14px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FileText size={14} color={c.text2} />
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontSize: 12.5, fontWeight: 500, margin: '0 0 2px' }}>
                            {file.fileName}
                          </p>
                          <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{file.mimeType}</p>
                        </div>
                      </div>
                      <Btn
                        variant="secondary"
                        onClick={() => handleDownloadTextFile(file)}
                        style={{ flexShrink: 0 }}
                      >
                        <Download size={13} />
                        Download
                      </Btn>
                    </div>
                    <pre
                      style={{
                        margin: 0,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: c.surface2,
                        border: `1px solid ${c.border}`,
                        color: c.text2,
                        fontSize: 11.5,
                        lineHeight: 1.6,
                        fontFamily: 'IBM Plex Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
                        whiteSpace: 'pre-wrap',
                        overflowX: 'auto',
                      }}
                    >
                      {file.contents || '(empty file)'}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 8px', lineHeight: 1.6 }}>
                {PACK_PLACEHOLDER_MESSAGE}
              </p>
              <p style={{ fontSize: 11.5, color: c.text3, margin: 0, lineHeight: 1.6 }}>
                Your claim is saved, but this lyrics-template pack does not have any files yet.
              </p>
            </div>
          )
        ) : viewingBonus?.kind === 'sample-pack-files' ? (
          viewingSamplePrompts.length > 0 ? (
            <div>
              <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 10px', lineHeight: 1.6 }}>
                Choose a cinematic loop direction to use in your next creation.
              </p>
              <p style={{ fontSize: 11.5, color: c.text3, margin: '0 0 12px' }}>
                {viewingSamplePrompts.length} loop prompt
                {viewingSamplePrompts.length === 1 ? '' : 's'} in this pack
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {viewingSamplePrompts.map((entry, index) => {
                  const promptKey = `${entry.title}-${index}`;
                  return (
                    <div
                      key={promptKey}
                      style={{
                        border: `1px solid ${c.border}`,
                        borderRadius: 12,
                        background: c.surface1,
                        padding: '12px 14px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                        }}
                      >
                        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Layers size={14} color={c.text2} />
                          <p style={{ fontSize: 12.5, fontWeight: 500, margin: 0 }}>
                            {entry.title}
                          </p>
                        </div>
                        <Btn
                          onClick={() => handleUsePack('sample', viewingBonus, index, entry.title)}
                          style={{ flexShrink: 0 }}
                        >
                          Use pack
                        </Btn>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 8px', lineHeight: 1.6 }}>
                {PACK_PLACEHOLDER_MESSAGE}
              </p>
              <p style={{ fontSize: 11.5, color: c.text3, margin: 0, lineHeight: 1.6 }}>
                Your claim is saved, but this sample pack does not have any loop prompts yet.
              </p>
            </div>
          )
        ) : viewingBonus?.kind === 'voice-pack-placeholder' && viewingVoicePacks.length > 0 ? (
          <div>
            <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 10px', lineHeight: 1.6 }}>
              Choose up to two vocal directions to use in your next creation.
            </p>
            <p style={{ fontSize: 11.5, color: c.text3, margin: '0 0 12px', lineHeight: 1.6 }}>
              {viewingVoicePacks.length} child pack{viewingVoicePacks.length === 1 ? '' : 's'} in
              this bonus
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {viewingVoicePacks.map((voicePack, index) => {
                const voicePackKey = `${voicePack.title}-${index}`;
                const voicePackName = `${viewingBonus.name} — ${voicePack.title}`;
                return (
                  <div
                    key={voicePackKey}
                    style={{
                      border: `1px solid ${c.border}`,
                      borderRadius: 12,
                      background: c.surface1,
                      padding: '12px 14px',
                    }}
                  >
                    <div
                      style={{
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                      }}
                    >
                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Music size={14} color={c.text2} />
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontSize: 12.5, fontWeight: 500, margin: 0 }}>
                            {voicePackName}
                          </p>
                        </div>
                      </div>
                      <Btn
                        onClick={() => handleUsePack('voice', viewingBonus, index, voicePack.title)}
                        style={{ flexShrink: 0 }}
                      >
                        Use pack
                      </Btn>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 8px', lineHeight: 1.6 }}>
              {viewingBonus?.note || PACK_PLACEHOLDER_MESSAGE}
            </p>
            <p style={{ fontSize: 11.5, color: c.text3, margin: 0, lineHeight: 1.6 }}>
              Your claim is saved and this bonus will unlock once delivery is ready.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
